import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useFetchAuto } from '@rad/hooks';
import { getAnalyticsReport, getCatalog } from './api';
import { formatIdentifier } from './format';
import { AccessGate } from './components/AccessGate';
import { ApplicationDetail } from './components/ApplicationDetail';
import { Brand } from './components/Brand';
import { Overview } from './components/Overview';
const accessKey = 'nexus.reporting.preview';
const readEmail = () => {
    try {
        const value = JSON.parse(sessionStorage.getItem(accessKey) || 'null');
        return value?.email;
    }
    catch {
        return undefined;
    }
};
const displayName = (email) => email
    .split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map(part => part[0].toUpperCase() + part.slice(1))
    .join(' ') || 'Analyst';
const AnalyticsDashboard = ({ email, onSignOut }) => {
    const [period, setPeriod] = React.useState(30);
    const [currentApp, setCurrentApp] = React.useState();
    const report = useFetchAuto(getAnalyticsReport(period), [period]);
    const catalog = useFetchAuto(getCatalog(), []);
    const name = displayName(email);
    const appNames = React.useMemo(() => new Map([
        ['hub', 'Nexus Hub'],
        ...(catalog.data ?? []).map(application => [application.id, application.name]),
    ]), [catalog.data]);
    const appName = (id) => appNames.get(id) || formatIdentifier(id);
    const showOverview = (anchor) => {
        setCurrentApp(undefined);
        if (anchor)
            window.setTimeout(() => document.querySelector(anchor)?.scrollIntoView({ 'behavior': 'smooth' }), 0);
    };
    return _jsxs("div", { className: "dashboard", children: [_jsxs("aside", { className: "sidebar", children: [_jsx(Brand, {}), _jsxs("nav", { "aria-label": "Primary navigation", children: [_jsxs("a", { className: !currentApp ? 'nav-item active' : 'nav-item', href: "#overview", onClick: () => showOverview(), children: [_jsx("span", { className: "nav-icon", children: "\u2301" }), "Overview"] }), _jsxs("a", { className: "nav-item", href: "#applications", onClick: () => showOverview('#applications'), children: [_jsx("span", { className: "nav-icon", children: "\u25EB" }), "Applications"] }), _jsxs("a", { className: "nav-item", href: "#activity", onClick: () => showOverview('#activity'), children: [_jsx("span", { className: "nav-icon", children: "\u2197" }), "Activity"] })] }), _jsxs("div", { className: "sidebar-foot", children: [_jsx("div", { className: "user-avatar", children: name[0].toUpperCase() }), _jsxs("div", { className: "user-copy", children: [_jsx("strong", { children: name }), _jsx("span", { children: email })] }), _jsx("button", { className: "sign-out", type: "button", onClick: onSignOut, "aria-label": "Sign out", children: "\u21AA" })] })] }), _jsx("main", { className: "dashboard-main", id: "overview", children: currentApp ? _jsx(ApplicationDetail, { applicationId: currentApp, applicationName: appName(currentApp), days: period, onBack: () => showOverview('#applications') }) : _jsx(Overview, { report: report.data, error: report.error, loading: report.loading, period: period, appName: appName, onPeriod: setPeriod, onRefresh: report.reload, onApplication: applicationId => { setCurrentApp(applicationId); window.scrollTo({ 'top': 0, 'behavior': 'smooth' }); } }) })] });
};
const App = () => {
    const [email, setEmail] = React.useState(readEmail);
    const authenticate = (value) => {
        try {
            sessionStorage.setItem(accessKey, JSON.stringify({ 'email': value }));
        }
        catch { /* Tab persistence is optional. */ }
        setEmail(value);
    };
    const signOut = () => {
        try {
            sessionStorage.removeItem(accessKey);
        }
        catch { /* Ignore blocked storage. */ }
        setEmail(undefined);
    };
    return email
        ? _jsx(AnalyticsDashboard, { email: email, onSignOut: signOut })
        : _jsx(AccessGate, { onAuthenticated: authenticate });
};
export default App;
